<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { QuestionFilled } from '@element-plus/icons-vue'
import { ref } from 'vue'
import { useRoomStore } from '@/stores/modules/room'
import { crackRank } from '../data'
import { onMounted } from 'vue'
import RelicRuinMonsterDetail from '@/components/relic-ruin-monster-detail/RelicRuinMonsterDetail.vue'
import { explainRelicIdList } from '@/core/relic'

const envMode = import.meta.env.VITE_ENV_MODE

const userStore = useUserStore()
const roomStore = useRoomStore()

defineProps<{ monster?: NestMonsterType }>()
const emits = defineEmits(['getMonster', 'start', 'giveUp', 'showRoom'])

const rank = ref(1)
const localKey = `war-soul-${envMode === 'test' ? 'test-' : ''}relic-ruin-rank`
function handleChangeRank() {
  localStorage.setItem(localKey, String(rank.value))
}
function handleHuntRelicRuinMonster() {
  if (userStore.relicRuinTimes <= 0) return ElMessage.warning('无次数时无法进行探索')

  ElMessageBox.confirm(
    `<p class="affix">确定<span class="info">探索</span>【<span class="relic">圣物遗迹</span>】吗？（本次探索的难度为<span class="${
      crackRank[rank.value].class
    }">${crackRank[rank.value].name}</span>）</p>`,
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'huntRelicRuinMonster', data: { rank: rank.value } })
    emits('getMonster', res)
    ElMessage.success('探索成功')
  })
}
async function handleStartRelicRuin() {
  if (roomStore.id) return start()
  ElMessageBox.confirm(
    '<p class="affix">确定<span class="hp">独自</span>进行【<span class="relic">圣物遗迹</span>】<span class="info">战斗</span>吗？</p>',
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(() => start())

  async function start() {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'startRelicRuinFight' })
    roomShow.value = false
    roomStore.clearRoomInfo()
    emits('start', { ...res, type: 'relicRuin' })
  }
}
function handleRefreshRelicRuin() {
  const needDiamond = userStore.vipLevel ? 100 : 200
  if (userStore.diamond < needDiamond) return ElMessage.warning(`重新探索需要 ${needDiamond}钻石`)
  if (roomStore.id) return ElMessage.warning('无法在房间内时重新探索魔物')

  ElMessageBox.confirm(
    `<p class="affix">确定<span class="hp">放弃</span>探索当前遗迹并进行重新<span class="info">探索</span>吗？（消耗 <span class="diamond">${needDiamond}</span>钻石，本次探索的难度为<span class="${
      crackRank[rank.value].class
    }">${crackRank[rank.value].name}</span>）</p>`,
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'refreshRelicRuinMonster', data: { rank: rank.value } })
    userStore.diamond -= needDiamond
    emits('getMonster', res)
    ElMessage.success('重新探索成功')
  })
}

const roomShow = ref(false)
function handleOpenRoom() {
  emits('showRoom')
}

onMounted(() => {
  const localRank = localStorage.getItem(localKey)
  if (localRank) rank.value = Number(localRank)
})
</script>

<template>
  <div class="border-wrap relic-ruin">
    <h4 class="relic">
      圣物遗迹
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix" style="max-height: 60vh; overflow: auto">
            <h4>圣物遗迹说明</h4>
            <p>1. 圣物遗迹<span class="info">每周日0点刷新</span>，刷新后将重置奖励次数。</p>
            <p>
              2. 通过<span class="info">探索</span>，您会随机遭遇一只<span class="info">无尽之塔怪物</span>，同时得知<span class="relic"
                >圣物线索</span
              >以及<span class="info">圣物藏宝图出率</span>。
            </p>
            <p>
              3. 通过圣物遗迹战斗您将必定获得 对应<span class="info">线索</span>的 2件圣物（可能重复），并有概率获得<span class="relic"
                >圣物藏宝图</span
              >。
            </p>
            <p>
              4. <span class="info">难度</span>越高的怪物挑战难度越大，同时随难度提升，探索的<span class="relic">圣物线索</span
              >中的圣物高品质概率将增加。
            </p>
            <p>5. 圣物遗迹支持<span class="info">双人组队</span>，当组队挑战时，怪物的血量增加<span class="grow">0.7</span>倍。</p>
            <p>6. 战斗时支持<span class="info">切换装备即刻生效</span>，当输出不足时，请及时更换装备。</p>
            <p>7. 战斗失败时，<span class="grow">不会消耗</span>奖励次数；当奖励次数用尽时，您无法进行探索及获得奖励，但您仍可以帮助其他玩家战斗。</p>
          </div>
        </el-popover>
      </div>
    </h4>
    <p style="display: flex; align-items: center">
      难度：
      <el-select size="small" v-model="rank" :class="crackRank[rank].class" style="width: 90px" @change="handleChangeRank">
        <el-option v-for="item of [1, 2, 3, 4]" :key="item" :label="crackRank[item].name" :value="item">
          <span :class="crackRank[item].class">{{ crackRank[item].name }}</span>
        </el-option>
      </el-select>
    </p>
    <p>
      <span>剩余奖励次数：{{ userStore.relicRuinTimes }}</span>
    </p>
    <p>
      当前怪物：
      <RelicRuinMonsterDetail :monster="monster" />
    </p>
    <p v-if="monster?.relicIdList">
      <span> 圣物线索：<span v-html="explainRelicIdList(monster.relicIdList)"></span> </span>
    </p>
    <div style="margin: 10px 0">
      <div v-show="!monster?.name">
        <el-button type="warning" plain @click="handleHuntRelicRuinMonster" :disabled="userStore.advanceLevel !== 4">
          {{ userStore.advanceLevel !== 4 ? '进阶后解锁' : '探索' }}
        </el-button>
        <el-button type="primary" plain :disabled="!userStore.soulType" @click="handleOpenRoom">组队战斗</el-button>
      </div>
      <div v-show="monster?.name">
        <el-button type="success" plain @click="() => handleStartRelicRuin()" :disabled="Boolean(roomStore.id)">独自战斗</el-button>
        <el-button type="primary" plain @click="handleOpenRoom">组队战斗</el-button>
        <el-button type="danger" plain @click="handleRefreshRelicRuin">重新探索</el-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep() {
  .normal {
    .el-select__selected-item {
      span {
        color: #888084;
      }
    }
  }
  .race {
    .el-select__selected-item {
      span {
        color: #3e62ed;
      }
    }
  }
  .epic {
    .el-select__selected-item {
      span {
        color: #8d4bbb;
      }
    }
  }
  .antiquity {
    .el-select__selected-item {
      span {
        color: #f05654;
      }
    }
  }
}
</style>
